import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Eye,
  Sparkles,
  Loader2,
  ExternalLink,
  Layout,
  Type,
  Image,
  Users,
  HelpCircle,
  Palette,
  Code,
  Power,
  PowerOff,
  Trash2,
  Plus,
  X,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

export default function AdminLandingPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enhancing, setEnhancing] = useState({});
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    fetchPage();
  }, [id]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/landing-pages/${id}`);
      setPage(response.data);
    } catch (error) {
      console.error('Error fetching landing page:', error);
      toast.error('Failed to load landing page');
      navigate('/admin/landing-pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/landing-pages/${id}`, page);
      toast.success('Landing page saved successfully');
    } catch (error) {
      toast.error('Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const handleAIEnhance = async (section, style = 'professional') => {
    setEnhancing((prev) => ({ ...prev, [section]: true }));
    try {
      const response = await api.post(`/landing-pages/${id}/ai-enhance`, {
        section,
        style,
      });
      
      // Update page with AI-generated content
      const generated = response.data.generated;
      
      if (section === 'hero') {
        setPage((prev) => ({
          ...prev,
          hero_headline: generated.headline || prev.hero_headline,
          hero_subheadline: generated.subheadline || prev.hero_subheadline,
          hero_cta_text: generated.cta_text || prev.hero_cta_text,
        }));
      } else if (section === 'outcomes') {
        setPage((prev) => ({
          ...prev,
          learning_outcomes: Array.isArray(generated) ? generated : prev.learning_outcomes,
        }));
      } else if (section === 'faq') {
        setPage((prev) => ({
          ...prev,
          faq_items: Array.isArray(generated) ? generated : prev.faq_items,
        }));
      } else if (section === 'about') {
        setPage((prev) => ({
          ...prev,
          about_content: generated.content || prev.about_content,
        }));
      }
      
      toast.success(`${section} section enhanced with AI`);
    } catch (error) {
      console.error('AI enhance error:', error);
      toast.error(error.response?.data?.detail || 'AI enhancement failed');
    } finally {
      setEnhancing((prev) => ({ ...prev, [section]: false }));
    }
  };

  const handleToggleStatus = async () => {
    try {
      if (page.is_enabled) {
        await api.post(`/landing-pages/${id}/disable`);
        setPage((prev) => ({ ...prev, is_enabled: false, status: 'draft' }));
        toast.success('Landing page disabled');
      } else {
        await api.post(`/landing-pages/${id}/enable`);
        setPage((prev) => ({ ...prev, is_enabled: true, status: 'active' }));
        toast.success('Landing page enabled');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateField = (field, value) => {
    setPage((prev) => ({ ...prev, [field]: value }));
  };

  const addOutcome = () => {
    setPage((prev) => ({
      ...prev,
      learning_outcomes: [...(prev.learning_outcomes || []), ''],
    }));
  };

  const updateOutcome = (index, value) => {
    setPage((prev) => ({
      ...prev,
      learning_outcomes: prev.learning_outcomes.map((o, i) => (i === index ? value : o)),
    }));
  };

  const removeOutcome = (index) => {
    setPage((prev) => ({
      ...prev,
      learning_outcomes: prev.learning_outcomes.filter((_, i) => i !== index),
    }));
  };

  const addFAQ = () => {
    setPage((prev) => ({
      ...prev,
      faq_items: [...(prev.faq_items || []), { question: '', answer: '' }],
    }));
  };

  const updateFAQ = (index, field, value) => {
    setPage((prev) => ({
      ...prev,
      faq_items: prev.faq_items.map((faq, i) =>
        i === index ? { ...faq, [field]: value } : faq
      ),
    }));
  };

  const removeFAQ = (index) => {
    setPage((prev) => ({
      ...prev,
      faq_items: prev.faq_items.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  if (!page) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="landing-page-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/landing-pages">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy-700">
              Edit Landing Page
            </h1>
            <p className="text-slate-500 text-sm">/{page.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={page.is_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
            {page.is_enabled ? 'Active' : 'Draft'}
          </Badge>
          <Button
            variant="outline"
            onClick={handleToggleStatus}
          >
            {page.is_enabled ? (
              <>
                <PowerOff className="w-4 h-4 mr-2" />
                Disable
              </>
            ) : (
              <>
                <Power className="w-4 h-4 mr-2" />
                Enable
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/lp/${page.slug}`, '_blank')}
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-navy-600 hover:bg-navy-700"
            data-testid="save-landing-page-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="hero" className="flex items-center gap-2">
            <Layout className="w-4 h-4" />
            Hero
          </TabsTrigger>
          <TabsTrigger value="about" className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            About
          </TabsTrigger>
          <TabsTrigger value="outcomes" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Outcomes
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Hero Section</CardTitle>
                <CardDescription>The main banner of your landing page</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => handleAIEnhance('hero')}
                disabled={enhancing.hero}
                data-testid="ai-enhance-hero-btn"
              >
                {enhancing.hero ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Enhance
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Headline</Label>
                <Input
                  value={page.hero_headline || ''}
                  onChange={(e) => updateField('hero_headline', e.target.value)}
                  placeholder="Enter a compelling headline..."
                />
              </div>
              <div className="space-y-2">
                <Label>Subheadline</Label>
                <Textarea
                  value={page.hero_subheadline || ''}
                  onChange={(e) => updateField('hero_subheadline', e.target.value)}
                  placeholder="Enter a supporting subheadline..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Button Text</Label>
                <Input
                  value={page.hero_cta_text || ''}
                  onChange={(e) => updateField('hero_cta_text', e.target.value)}
                  placeholder="Register Now"
                />
              </div>
              <div className="space-y-2">
                <Label>Hero Image URL</Label>
                <Input
                  value={page.hero_image_url || ''}
                  onChange={(e) => updateField('hero_image_url', e.target.value)}
                  placeholder="https://..."
                />
                {page.hero_image_url && (
                  <img
                    src={page.hero_image_url}
                    alt="Hero preview"
                    className="mt-2 rounded-lg max-h-48 object-cover"
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Section */}
        <TabsContent value="about" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>About Section</CardTitle>
                <CardDescription>Describe what this webinar is about</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => handleAIEnhance('about')}
                disabled={enhancing.about}
                data-testid="ai-enhance-about-btn"
              >
                {enhancing.about ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Enhance
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={page.about_content || ''}
                  onChange={(e) => updateField('about_content', e.target.value)}
                  placeholder="Describe your webinar in detail..."
                  rows={8}
                />
                <p className="text-xs text-slate-500">
                  Supports HTML formatting for rich content.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Learning Outcomes */}
        <TabsContent value="outcomes" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Learning Outcomes</CardTitle>
                <CardDescription>What attendees will learn</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => handleAIEnhance('outcomes')}
                disabled={enhancing.outcomes}
                data-testid="ai-enhance-outcomes-btn"
              >
                {enhancing.outcomes ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Generate
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {(page.learning_outcomes || []).map((outcome, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-500 w-6">
                    {index + 1}.
                  </span>
                  <Input
                    value={outcome}
                    onChange={(e) => updateOutcome(index, e.target.value)}
                    placeholder="Enter a learning outcome..."
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOutcome(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addOutcome} className="mt-2">
                <Plus className="w-4 h-4 mr-2" />
                Add Outcome
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQ Section */}
        <TabsContent value="faq" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>FAQ Section</CardTitle>
                <CardDescription>Common questions and answers</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => handleAIEnhance('faq')}
                disabled={enhancing.faq}
                data-testid="ai-enhance-faq-btn"
              >
                {enhancing.faq ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Generate
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {(page.faq_items || []).map((faq, index) => (
                <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <Label>Question {index + 1}</Label>
                      <Input
                        value={faq.question || ''}
                        onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                        placeholder="Enter the question..."
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFAQ(index)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label>Answer</Label>
                    <Textarea
                      value={faq.answer || ''}
                      onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                      placeholder="Enter the answer..."
                      rows={3}
                    />
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addFAQ}>
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Page Settings</CardTitle>
              <CardDescription>Customize the appearance of your landing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Page Title</Label>
                  <Input
                    value={page.title || ''}
                    onChange={(e) => updateField('title', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Page Slug</Label>
                  <Input
                    value={page.slug || ''}
                    onChange={(e) => updateField('slug', e.target.value)}
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-4">
                <h3 className="font-medium text-navy-700">Color Scheme</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={page.color_scheme?.primary || '#1B4F72'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            primary: e.target.value,
                          })
                        }
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={page.color_scheme?.primary || '#1B4F72'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            primary: e.target.value,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={page.color_scheme?.secondary || '#D4A03A'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            secondary: e.target.value,
                          })
                        }
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={page.color_scheme?.secondary || '#D4A03A'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            secondary: e.target.value,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={page.color_scheme?.accent || '#1B4F72'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            accent: e.target.value,
                          })
                        }
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={page.color_scheme?.accent || '#1B4F72'}
                        onChange={(e) =>
                          updateField('color_scheme', {
                            ...page.color_scheme,
                            accent: e.target.value,
                          })
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Custom CSS</Label>
                <Textarea
                  value={page.custom_css || ''}
                  onChange={(e) => updateField('custom_css', e.target.value)}
                  placeholder=".hero { background: linear-gradient(...); }"
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Add custom CSS to further customize your landing page appearance.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
